/**
 * Billing Context
 * Caches GET /api/billing for the signed-in user so the Sidebar plan badge,
 * workspace switcher and Billing page share one fetch.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { billingApi } from './api';
import type { BillingInfo } from './api';
import { useAuth } from './auth';

interface BillingContextType {
  billing: BillingInfo | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setBilling: (b: BillingInfo) => void;
  /** True for an Agency account, or while switched into one of its workspaces. */
  isAgency: boolean;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingApi.get();
      if (res.success && res.data) setBilling(res.data);
    } catch (err) {
      console.error('Billing load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const userId = user?.id;
  useEffect(() => {
    if (userId) refresh();
    else setBilling(null);
  }, [userId, refresh]);

  const isAgency = billing?.plan === 'agency' || !!user?.actor;

  return (
    <BillingContext.Provider value={{ billing, loading, refresh, setBilling, isAgency }}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling(): BillingContextType {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
